import moment from 'moment'
import { useMemo, useState } from 'react'

// Helper hook for filtering through lists of object by text
export const useFilter = <T extends object>(
  data: T[],
  filterFn?: (filter: string, val: T) => boolean
): [T[], (filter: string) => void, string] => {
  const [filter, setFilter] = useState('')
  const filterValue = filter.toLowerCase()

  const filteredData = useMemo(() => {
    if (!data) {
      return []
    }
    const filteredData = data.filter(item => {
      // First check against custom filter function with specialized logic
      if (filterFn && filterFn(filterValue, item)) {
        return true
      }
      const searchObject = (item: any, depth: number): boolean => {
        if (depth === 0 || !item) {
          return false
        }
        if (typeof item === 'string' || typeof item === 'number') {
          return item.toString().toLowerCase().includes(filterValue)
        }
        // If it's an object recursively check if any of the object properties contain the search filter
        if (typeof item === 'object') {
          return Object.keys(item).some(key => {
            return searchObject(item[key], depth - 1)
          })
        }
        return false
      }
      return searchObject(item, 4) // max depth incase of circular references
    })

    const sortedData = filteredData.sort((i1: any, i2: any) => {
      if (!i1 || !i2) return 0
      if (typeof i1 === 'string' && typeof i2 === 'string') {
        return getStringSortResult(i1, i2, filterValue)
      }

      let output = 0
      if (typeof i1 === 'object') {
        Object.keys(i1).forEach(key => {
          const v1 = i1[key]
          const v2 = i2[key]
          if (typeof v1 === 'string' && typeof v2 === 'string') {
            output = output + getStringSortResult(v1, v2, filterValue)
          }
        })
      }

      return output
    })
    return sortedData
  }, [data, filter])

  return useMemo(() => [filteredData, setFilter, filter], [filteredData, setFilter, filter])
}

const getStringSortResult = (s1: string, s2: string, filterValue: string) => {
  const item1 = s1.toLowerCase()
  const item2 = s2.toLowerCase()
  if (item1 === item2) return 0

  const item1HasPrio = item1.startsWith(filterValue)
  const item2HasPrio = item2.startsWith(filterValue)
  if (item1HasPrio && !item2HasPrio) return -1
  if (!item1HasPrio && item2HasPrio) return 1

  return 0
}

export const filterDateRange = (keyword: string, dateFromValue: moment.Moment, dateToValue?: moment.Moment): boolean => {
  const kAsNumber = Number(keyword)
  if (!isNaN(kAsNumber) && kAsNumber >= 1886) {
    // find the DateFrom and DateTo values.
    const inFromRange = dateFromValue.isValid() && kAsNumber >= dateFromValue.year()
    // when dateTo is not provided asume it's because there is no end yet
    const inToRange = !dateToValue || (dateToValue.isValid() && kAsNumber <= dateToValue.year())
    if (inFromRange && inToRange) {
      return true
    }
  }
  return false
}
